/* eslint-disable no-bitwise */
import { ShopFront } from '@csc/csc-sdk'
import Axios from 'axios'
import UsStates from '@/data/UsStates'
import CustomerInfo, { AddressType, BigCommerceAddress } from '@/types/CustomerInfo'
import { getPlacesPredictions } from '@/services/FramedCheckout/addressServices'
import { standardizeAddress } from '@/services/Standardizers'
import { OAUTH_API, CSC_PAYMENTS_SHOP } from './Configuration'
import { error } from '@/services/Log'
import { StandardToBigCommerceAddress } from './Standardizers/BigCommerceToStandardAddress'
import { getSavedInstrumentsCredentials } from '@/services/FramedCheckout/cscPaymentsServices'

const upsertCustomerAddress: (address: AddressType) => Promise<{ success: false, newAddress: null } | { success: true, newAddress: BigCommerceAddress }> = async (
  address: AddressType
) => {
  try {
    const payload = StandardToBigCommerceAddress(address)
    const { success, data: addresses } = (await Axios.post<{ success: boolean, data: BigCommerceAddress[] }>(`${OAUTH_API}/sf/customer/address/`,
      { ...payload, country_code: 'US' },
      { headers: { 'Content-Type': 'application/json' } })).data
    const newAddress = addresses.find(({ id }) => id === address.id)
    if (success && newAddress) {
      return { success: true, newAddress }
    }
  } catch (err) {
    error(`upsertCustomerAddress Failed ${err}`, { error: err })
  }
  return { success: false, newAddress: null }
}


const createAddress: (address: AddressType) => Promise<{ success: boolean }> = async (
  address: AddressType
) => {
  try {
    const payload = StandardToBigCommerceAddress(address)
    const { success } = (
      await Axios.post<{ success: boolean }>(
        `${OAUTH_API}/sf/customer/address/`,
        { ...payload, country_code: 'US' },
        { headers: { 'Content-Type': 'application/json' } }
      )
    ).data

    return { success }

  } catch (err) {
    error(`upsertCustomerAddress Failed ${err}`, { error: err })
  }
  return { success: false }
}

const Account = {
  email: async () => {
    try {
      return (await Axios.get('/login.php', {
        headers: {
          'stencil-config': '{}',
          'stencil-options': '{"render_with":"customer/customer-json"}',
        },
      })).data
    } catch {
      error('Failed to retrieve Account email')
    }
  },

  createCustomer: (newCustomer: {
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    password: string,
    acceptsMarketingEmails: boolean,
  }) => Axios.post(`${OAUTH_API}/sf/customer`, newCustomer),

  updateCustomerInfo: async (payload: {
    first_name: string,
    last_name: string,
    email: string,
    phone: string,
    id: string,
    current_password: string,
    confirm_password: string,
    password: string,
  }) => (await Axios.post(`${OAUTH_API}/sf/customer/edit/`, {
    ...payload,
    password: payload.password || payload.current_password,
    confirm_password: payload.confirm_password || payload.current_password,
  }, {
    headers: { 'Content-Type': 'application/json' },
  })).data,

  upsertCustomerAddress,
  createAddress,
  checkValidityOfAddress: async (address: any) => {
    const { validAddress, addressPredictions } = await getPlacesPredictions(standardizeAddress(address))
    if (!validAddress && !addressPredictions?.length) {
      return {
        addressValid: false,
        displayMessage: 'Entered address is not correct.',
        displayButtons: false,
        suggestedAddress: null,
      }
    }

    if (validAddress) {
      return {
        addressValid: true,
        displayMessage: '',
        displayButtons: false,
        suggestedAddress: null,
      }
    }
    return {
      displayMessage: 'Did you mean: ',
      addressValid: false,
      displayButtons: true,
      suggestedAddress: {
        ...addressPredictions[0],
        state: `${addressPredictions[0].stateName || ''}`,
      }
    }
  },

  deleteCustomerAddress: async (payload: {
    id: string | number, // address_id
    customer_id: string | number,
  }) => (await Axios.delete(`${OAUTH_API}/sf/customer/address/`, {
    headers: { 'Content-Type': 'application/json' },
    data: payload,
  })).data,

  addressBook: async ({ customerEmail }) => (await Axios.get(
    `${OAUTH_API}/sf/customer/addresses/${customerEmail}`,
  )).data.data,

  getCustomerInfo: async (): Promise<CustomerInfo> => ((await Axios.post(
    `${OAUTH_API}/sf/customer/data/`,
  )).data?.data || [])[0],

  signUserIn: async ({ email, password }) => Axios.post(`${OAUTH_API}/login`, { email, password }),


  // Convenience method, tech debt - BigCommerce response format is bad, thus...
  transformSavedAddresses: ({ addresses }) => addresses.map(({
    city,
    address1,
    // address2,
    phone,
    first_name: firstName,
    last_name: lastName,
    postal_code: postalCode,
    state_or_province: stateOrProvince,
    country_code: countryCode,
  }) => ({
    id: address1,
    city,
    address1,
    postalCode,
    stateOrProvince,
    stateOrProvinceCode: UsStates.find((state) => state.name === stateOrProvince)?.abbreviation,
    countryCode,
    phone,
    firstName,
    lastName,
  })),
}

export default Account

export const migrateFromLegacy = async ({
  identifier,
  password,
}: {
  identifier: string
  password: string
}) => {
  try {
    const {
      accessKeyId,
      secretAccessKey,
      sessionToken,
    } = await getSavedInstrumentsCredentials()
    if (!accessKeyId) {
      return
    }
    await ShopFront({
      credentials: {
        accessKeyId,
        secretAccessKey,
        sessionToken,
      },
    }).migrateLegacyLoginProfile({
      identifier,
      password,
      shopName: CSC_PAYMENTS_SHOP,
    })
  } catch {
    console.log('Customer was NOT legacy migrated.')
  }
}
